import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { fetchNWSActive } from "../utils/nws";
import { PRODUCTS, nearestSite } from "./radarConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useRadarSites = () => {
  const [sites, setSites] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await axios.get(`${API}/radar/sites`);
        if (!cancelled) setSites(response.data || []);
      } catch (_) {}
    };
    load();
    const timer = setInterval(load, 120000);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);
  return sites;
};

export const useRadarScans = (productId, siteId, enabled = true) => {
  const [scans, setScans] = useState([]);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const site = productId === "composite" ? "USCOMP" : siteId;
    if (!site) return undefined;
    const load = async () => {
      try {
        const response = await axios.get(`${API}/radar/scans`, { params: { site, product: productId } });
        if (!cancelled) setScans(response.data?.scans || []);
      } catch (_) {}
    };
    load();
    const timer = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [productId, siteId, enabled]);
  return scans;
};

export const useStormReports = (enabled) => {
  const [reports, setReports] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const response = await axios.get(`${API}/radar/reports`, { params: { hours: 3 } });
        if (!cancelled) setReports(response.data);
      } catch (_) {}
    };
    load();
    const timer = setInterval(load, 180000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [enabled]);
  return reports;
};

export const useNWSAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const data = await fetchNWSActive();
      if (cancelled) return;
      setAlerts(data);
      setLastUpdated(new Date());
      setLoading(false);
    };
    load();
    const timer = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);
  return { alerts, loading, lastUpdated };
};

// Frame loop: frames are ordered oldest -> newest; dwell on the newest frame like RadarScope.
export const useRadarLoop = (frameCount, playing, speed = 1) => {
  const [frame, setFrame] = useState(frameCount - 1);
  useEffect(() => { setFrame(frameCount - 1); }, [frameCount]);
  useEffect(() => {
    if (!playing) return undefined;
    let timer;
    const step = () => {
      setFrame((current) => {
        const next = (current + 1) % frameCount;
        timer = setTimeout(step, next === frameCount - 1 ? 1400 / speed : 420 / speed);
        return next;
      });
    };
    timer = setTimeout(step, 420 / speed);
    return () => clearTimeout(timer);
  }, [playing, frameCount, speed]);
  return [frame, setFrame];
};

export const useFreshnessBucket = (ms = 120000) => {
  const [bucket, setBucket] = useState(() => Math.floor(Date.now() / ms));
  useEffect(() => {
    const timer = setInterval(() => setBucket(Math.floor(Date.now() / ms)), 30000);
    return () => clearInterval(timer);
  }, [ms]);
  return bucket;
};

export const useActiveSite = (sites, view, override) => useMemo(() => {
  if (override) return sites.find((site) => site.id === override) || null;
  if (!sites.length) return null;
  return nearestSite(sites, view.lat, view.lon);
}, [sites, view.lat, view.lon, override]);

export const useGeolocate = (mapRef, onLocated) => {
  const [locating, setLocating] = useState(false);
  const [location, setLocation] = useState(null);
  const onLocatedRef = useRef(onLocated);
  onLocatedRef.current = onLocated;
  const locate = useCallback(() => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const point = { lat: position.coords.latitude, lon: position.coords.longitude };
        setLocation(point);
        setLocating(false);
        mapRef.current?.flyTo([point.lat, point.lon], Math.max(mapRef.current.getZoom(), 8), { duration: 0.8 });
        onLocatedRef.current?.(point);
      },
      () => setLocating(false),
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
    );
  }, [mapRef]);
  return { locate, locating, location };
};

export const productById = (id) => PRODUCTS.find((product) => product.id === id) || PRODUCTS[0];
